import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Image as ImageIcon, Calendar, Newspaper, ShoppingBag,
  Camera, Users, Send, Search, Settings as SettingsIcon, LogOut, ArrowLeft, Mail, Trophy, BarChart3, Megaphone,
} from "lucide-react";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/banners", label: "Banners", icon: ImageIcon },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/match-results", label: "Match Results", icon: Trophy },
  { to: "/admin/blogs", label: "Blog & News", icon: Newspaper },
  { to: "/admin/products", label: "Products", icon: ShoppingBag },
  { to: "/admin/gallery", label: "Gallery", icon: Camera },
  { to: "/admin/stats", label: "Stats", icon: BarChart3 },
  { to: "/admin/notifications", label: "Notifications", icon: Megaphone },
  { to: "/admin/inquiries", label: "Inquiries", icon: Mail },
  { to: "/admin/subscribers", label: "Subscribers", icon: Users },
  { to: "/admin/messages", label: "Bulk Email/SMS", icon: Send },
  { to: "/admin/seo", label: "SEO", icon: Search },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="admin-shell min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 bg-[var(--pz-ink)] border-r border-[var(--pz-line)] flex-col">
        <div className="p-5 border-b border-[var(--pz-line)]">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-8 w-auto object-contain" />
            <div className="leading-none">
              <div className="font-display text-white font-bold uppercase tracking-wide">Admin</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Control Room</div>
            </div>
          </Link>
          <Link to="/" className="text-xs text-zinc-500 mt-3 inline-flex items-center gap-1 hover:text-white uppercase tracking-[0.15em]"><ArrowLeft className="h-3 w-3" /> Back to site</Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-auto">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => `admin-link ${isActive ? "active" : ""}`}
              >
                <Icon className="h-4 w-4" /> {l.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[var(--pz-line)]">
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 truncate">{admin?.email}</div>
          <button data-testid="admin-logout-btn" onClick={() => { logout(); nav("/admin/login"); }} className="admin-link w-full text-left hover:!bg-rose-500/10 hover:!text-rose-400">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* mobile bar */}
        <div className="lg:hidden bg-[var(--pz-ink)] border-b border-[var(--pz-line)] px-4 h-14 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-white">
            <span className="relative h-8 w-8 grid place-items-center">
              <span className="absolute inset-0 bg-[var(--pz-red)] rotate-45 rounded" />
              <span className="relative font-display text-white text-sm leading-none">A</span>
            </span>
            <span className="font-display font-bold uppercase">Admin</span>
          </Link>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="text-rose-400 text-xs font-bold uppercase tracking-[0.15em] inline-flex items-center gap-1"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
        <div className="lg:hidden bg-[var(--pz-ink)] border-b border-[var(--pz-line)] px-3 py-2 overflow-x-auto flex gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-bold ${isActive ? "bg-[var(--pz-red)] text-white" : "bg-[var(--pz-ink-2)] text-zinc-400"}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <main className="p-5 md:p-8 bg-[#0B0C0D] min-h-[calc(100vh-56px)] lg:min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
